import styled from 'styled-components';
import {
  faAngleDown,
  faQuestion,
  faInfoCircle,
  faAngleUp,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useRef, useContext, useEffect } from 'react';
import { ReactReduxContext } from 'react-redux';
import strings from '../../../translations';
import {
  setIsInfoOpen,
  setIsProfileOpen,
  setIsUserGuideOpen
} from '../../../state/slices/uiSlice';
import { IS_EXTRANET } from '../../../utils/appInfoUtil';

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

const DesktopNavContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.5em;
  isolation: isolate;
  z-index: 11;
`;

const ProfileButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(props) =>
    IS_EXTRANET ? props.theme.colors.mainColor1 : props.theme.colors.mainWhite};
  padding: 0;
  margin: 0;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    color: ${(props) => IS_EXTRANET ? props.theme.colors.mainColorselected1 : props.theme.colors.hover};
  }
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 110%;
  right: 0;
  min-width: 200px;
  background: ${(props) => props.theme.colors.mainWhite};
  color: ${(props) => props.theme.colors.black};
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.13);
  margin: 0;
  padding: 0;
  z-index: 13;
  list-style: none;
  font-size: 15px;
`;

const DropdownMenuItem = styled.li`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: none;
  color: ${(props) => props.theme.colors.black};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  border-radius: 0; /* default: no rounding for middle items */

  /* top corners on the first item */
  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  /* bottom corners on the last item */
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  /* if there's only one item, round all corners */
  &:only-child {
    border-radius: 8px;
  }

  &:hover,
  &:focus {
    background: ${(props) => props.theme.colors.hover};
    color: ${(props) => props.theme.colors.mainColor1};
    outline: none;
  }

  &.logout {
    justify-content: center;
    padding-top: 18px;
    padding-bottom: 18px;
    background: none;
    &:hover {
      background: none;
      color: ${(props) => props.theme.colors.black};
    }
    /* ensure logout preserves first/last radius when positioned accordingly */
  }

  svg,
  .MuiSvgIcon-root {
    color: ${(props) => props.theme.colors.mainColor1};
    min-width: 21px;
    min-height: 21px;
    font-size: 20px;
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
  }
  span {
    display: inline-block;
    vertical-align: middle;
  }
`;

const DesktopNav = ({ setIsMenuOpen, isMenuOpen }) => {
  const ref = useRef();
  useOnClickOutside(ref, () => setIsMenuOpen(false));
  const { store } = useContext(ReactReduxContext);

  // Menu actions
  const handleProfile = () => {
    store.dispatch(setIsProfileOpen(true));
    setIsMenuOpen(false);
  };

  const handleUserGuide = () => {
    store.dispatch(setIsUserGuideOpen(true));
    setIsMenuOpen(false);
  };

  const handleInfo = () => {
    store.dispatch(setIsInfoOpen(true));
    setIsMenuOpen(false);
  };

  // IDs for aria-controls/aria-labelledby
  const menuId = 'desktop-nav-profile-dropdown';
  const profileBtnId = 'desktop-nav-profile-btn';
  const menuProfileId = 'desktop-nav-profile-menuitem';
  const menuUserGuideId = 'desktop-nav-userguide-menuitem';
  const menuInfoId = 'desktop-nav-info-menuitem';

  return (
    <DesktopNavContainer ref={ref} id="desktop-nav-container">
      <ProfileButton
        aria-label={strings.accessibility?.openProfileMenu}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        aria-controls={menuId}
        id={profileBtnId}
        tabIndex={0}
        onClick={() => setIsMenuOpen((v) => !v)}
        title={strings.accessibility?.openProfileMenu}
      >
        {IS_EXTRANET ? (
          <>
            <AccountCircleIcon fontSize="large" />
            <FontAwesomeIcon
              style={{ marginLeft: '6px', fontSize: '19px' }}
              icon={isMenuOpen ? faAngleUp : faAngleDown}
              aria-hidden="true"
            />
          </>
        ) : (
          <>
            <FontAwesomeIcon
              icon={faBars}
              style={{ marginLeft: '6px', fontSize: '1.4em' }}
              aria-hidden="true"
              focusable="false"
            />
          </>
        )}
      </ProfileButton>
      {isMenuOpen && (
        <DropdownMenu id={menuId} role="menu" aria-labelledby={profileBtnId}>
          {IS_EXTRANET && (
            <DropdownMenuItem
              id={menuProfileId}
              role="menuitem"
              tabIndex={0}
              onClick={handleProfile}
              aria-label={strings.menu?.profile}
            >
              <AccountCircleIcon aria-hidden="true" />
              {strings.tooltips?.profile}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            id={menuUserGuideId}
            role="menuitem"
            tabIndex={0}
            onClick={handleUserGuide}
            aria-label={strings.tooltips.showUserGuide}
          >
            <FontAwesomeIcon icon={faQuestion} aria-hidden="true" />
            {strings.tooltips.userGuide}
          </DropdownMenuItem>
          <DropdownMenuItem
            id={menuInfoId}
            role="menuitem"
            tabIndex={0}
            onClick={handleInfo}
            aria-label={strings.tooltips.showPageInfo}
          >
            <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
            {strings.tooltips.pageInfo}
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    </DesktopNavContainer>
  );
};

export default DesktopNav;
